/*
 * Admin bulk question audit: reading a chosen folder into résumé / job
 * description pairs, and preparing each pair for upload (BULK_AUDIT_PLAN.md).
 *
 * Pairs are found two ways:
 *   - a sub-folder holding one résumé and one job description is a pair
 *     (NEW SCENARIOS: S01_.../Resume.docx + Job_Description.docx);
 *   - loose files in the chosen folder pair by a shared name or number
 *     (cv04.docx + jd04.docx, cv_10_role.docx + jd_10_role.docx).
 * The admin then ticks the pairs to run, re-pairs files or adds pairs by hand.
 *
 * Every résumé goes through the same browser privacy scan as the upload page
 * (`sanitizeResume`) before anything is sent; the original files never leave
 * the browser.
 */

export const MAX_PAIRS = 100;
export const JD_MAX_CHARS = 4000;
export const CALLS_PER_PAIR = 6;

const RESUME_EXT = /\.(docx|pdf)$/i;
const JD_EXT = /\.(docx|pdf|txt)$/i;

function stem(name) {
  return name
    .replace(/\.[^.]+$/, "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

const JD_WORD = /job[ _-]?description|job[ _-]?posting|posting|jd/;
const RESUME_WORD = /resume|cv/;

function hasWord(n, word) {
  // At the start ("cv04", "jd_10") or between non-letters ("S01_Resume").
  return new RegExp(`^(?:${word.source})|(?:^|[^a-z])(?:${word.source})(?=[^a-z]|$)`).test(n);
}

/** "resume", "jd" or null for a file name. */
export function roleOf(name) {
  const n = stem(name);
  if (JD_EXT.test(name) && hasWord(n, JD_WORD)) return "jd";
  if (RESUME_EXT.test(name) && hasWord(n, RESUME_WORD)) return "resume";
  return null;
}

/** The part of a loose file's name that pairs it: "cv04" and "jd04" give "04". */
export function pairKey(name) {
  return stem(name)
    .replace(new RegExp(`^(?:${JD_WORD.source})|(?:^|[^a-z])(?:${JD_WORD.source})(?=[^a-z]|$)`), " ")
    .replace(new RegExp(`^(?:${RESUME_WORD.source})|(?:^|[^a-z])(?:${RESUME_WORD.source})(?=[^a-z]|$)`), " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/ /g, "_");
}

function relPath(file) {
  return file.webkitRelativePath || file.name;
}

function safeId(raw) {
  return (raw || "pair")
    .replace(/[^A-Za-z0-9 ._-]+/g, "_")
    .replace(/^[^A-Za-z0-9]+/, "")
    .slice(0, 80) || "pair";
}

/**
 * Group the files of a chosen folder.
 * Returns { pairs: [{id, resumePath, jdPath}], files: [{path, name, role}],
 *           unpaired: [paths], expectedPath }.
 */
export function groupFolderFiles(fileList) {
  const files = Array.from(fileList || [])
    .map((f) => ({ path: relPath(f), name: f.name, role: roleOf(f.name), file: f }))
    .sort((a, b) => a.path.localeCompare(b.path));
  const expected = files.find((f) => /(^|\/)expected\.csv$/i.test(f.path) && f.path.split("/").length <= 2);

  const byDir = new Map();
  for (const f of files) {
    const parts = f.path.split("/");
    const dir = parts.slice(0, -1).join("/");
    if (!byDir.has(dir)) byDir.set(dir, []);
    byDir.get(dir).push(f);
  }
  // A chosen folder's paths all start with its name ("NEW SCENARIOS/...").
  const rootDir = files.length && files.every((f) => f.path.includes("/")) ? files[0].path.split("/")[0] : "";

  const pairs = [];
  const used = new Set();
  const ids = new Set();
  const addPair = (id, resume, jd) => {
    let unique = safeId(id);
    for (let n = 2; ids.has(unique.toLowerCase()); n++) unique = `${safeId(id)}_${n}`;
    ids.add(unique.toLowerCase());
    pairs.push({ id: unique, resumePath: resume.path, jdPath: jd.path });
    used.add(resume.path);
    used.add(jd.path);
  };

  for (const [dir, group] of byDir) {
    const resumes = group.filter((f) => f.role === "resume");
    const jds = group.filter((f) => f.role === "jd");
    if (dir !== rootDir && resumes.length === 1 && jds.length === 1) {
      addPair(dir.split("/").pop(), resumes[0], jds[0]);
      continue;
    }
    // Loose files: pair a résumé and a job description with the same key.
    const keyed = new Map();
    for (const f of [...resumes, ...jds]) {
      const key = pairKey(f.name);
      if (!key) continue;
      if (!keyed.has(key)) keyed.set(key, { resume: [], jd: [] });
      keyed.get(key)[f.role].push(f);
    }
    for (const [key, g] of keyed) {
      if (g.resume.length === 1 && g.jd.length === 1) addPair(key, g.resume[0], g.jd[0]);
    }
  }

  pairs.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: "base" }));
  const unpaired = files
    .filter((f) => f.role && !used.has(f.path))
    .map((f) => f.path);
  return {
    pairs,
    files: files.map(({ path, name, role }) => ({ path, name, role })),
    filesByPath: Object.fromEntries(files.map((f) => [f.path, f.file])),
    unpaired,
    expectedPath: expected ? expected.path : null,
  };
}

const LANGUAGE_VALUES = { accept: "accept", accepted: "accept", reject: "reject", rejected: "reject" };
const FIT_VALUES = {
  warn: "warn", warning: "warn", yes: "warn",
  no_warning: "no_warning", "no warning": "no_warning", none: "no_warning", no: "no_warning",
};

function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/**
 * expected.csv: columns pair_id, expected_language (accept|reject) and
 * expected_fit (warn|no_warning). Returns { [pair_id lower]: {language, fit} }.
 */
export function parseExpectedCsv(text) {
  const lines = (text || "").replace(/^﻿/, "").split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return {};
  const header = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
  const col = (name) => header.indexOf(name);
  const idCol = col("pair_id");
  if (idCol < 0) return {};
  const langCol = col("expected_language");
  const fitCol = col("expected_fit");
  const result = {};
  for (const line of lines.slice(1)) {
    const cells = splitCsvLine(line);
    const id = (cells[idCol] || "").toLowerCase();
    if (!id) continue;
    const language = LANGUAGE_VALUES[(cells[langCol] || "").toLowerCase()] || null;
    const fit = FIT_VALUES[(cells[fitCol] || "").toLowerCase()] || null;
    result[id] = { language, fit };
  }
  return result;
}

export function estimateCalls(pairCount) {
  return pairCount * CALLS_PER_PAIR;
}

/**
 * Read and check one pair in the browser. The résumé goes through the upload
 * page's privacy scan; only its cleaned text is returned. Returns
 * { ok: true, resume_text, jd_text } or { ok: false, problem }.
 */
export async function preparePair(pair, filesByPath, { sanitize, extract }) {
  const resumeFile = filesByPath[pair.resumePath];
  const jdFile = filesByPath[pair.jdPath];
  if (!resumeFile) return { ok: false, problem: "Résumé file missing" };
  if (!jdFile) return { ok: false, problem: "Job description file missing" };
  let resumeText;
  try {
    resumeText = (await sanitize(resumeFile)).text;
  } catch (e) {
    return { ok: false, problem: `Résumé unreadable (${(e && e.code) || "error"})` };
  }
  let jdText;
  try {
    jdText = await extract(jdFile);
  } catch (e) {
    return { ok: false, problem: `Job description unreadable (${(e && e.code) || "error"})` };
  }
  if (jdText.length > JD_MAX_CHARS) {
    return { ok: false, problem: `Job description is ${jdText.length} characters (limit ${JD_MAX_CHARS})` };
  }
  return { ok: true, resume_text: resumeText, jd_text: jdText };
}

export const PAIR_STATUS_LABELS = {
  waiting: "Waiting",
  running: "Running",
  done: "Questions generated",
  privacy_blocked: "Stopped at privacy",
  language_blocked: "Stopped at language",
  fit_warning: "Fit warning",
  error: "Error",
  cancelled: "Cancelled",
};

export function batchIsActive(status) {
  return status === "queued" || status === "running" || status === "stopping";
}
